import React, { useContext } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  useLocation,
  Link,
} from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Navbar, Nav, Container, Row, Col, NavDropdown } from "react-bootstrap";
import {
  FaHome,
  FaStore,
  FaUsers,
  FaBed,
  FaUser,
  FaSignOutAlt,
  FaSignInAlt,
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaFacebook,
  FaTwitter,
  FaInstagram,
  FaLinkedin,
  FaYoutube,
  FaPlusCircle,
  FaComments,
  FaCrown,
} from "react-icons/fa";
import Home from "./pages/Home";
import Login from "./pages/Login";
import VerifyOTP from "./pages/VerifyOTP";
import BuyRentSell from "./pages/BuyRentSell";
import Workers from "./pages/Workers";
import Rooms from "./pages/Rooms";
import Profile from "./pages/Profile";
import PostListing from "./pages/PostListing";
import PostRoom from "./pages/PostRoom";
import MarketplaceListings from "./pages/MarketplaceListings";
import Careers from "./pages/Careers";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import PurchaseRequests from "./pages/PurchaseRequests";
import Chats from "./pages/Chats";
import AdminAds from "./pages/AdminAds";
import AdminVerifyWorkers from "./pages/AdminVerifyWorkers";
import PrivateRoute from "./components/PrivateRoute";
import { AuthContext } from "./context/AuthContext";

// Animation wrapper for pages
const pageTransition = { duration: 0.5 };
const AnimatedPage = ({ children, ...props }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -20 }}
    transition={pageTransition}
    {...props}
  >
    {children}
  </motion.div>
);

function AnimatedRoutes() {
  const location = useLocation();
  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route
          path="/"
          element={
            <AnimatedPage>
              <Home />
            </AnimatedPage>
          }
        />
        <Route
          path="/login"
          element={
            <AnimatedPage>
              <Login />
            </AnimatedPage>
          }
        />
        <Route
          path="/verify"
          element={
            <AnimatedPage>
              <VerifyOTP />
            </AnimatedPage>
          }
        />
        <Route
          path="/terms"
          element={
            <AnimatedPage>
              <Terms />
            </AnimatedPage>
          }
        />
        <Route
          path="/privacy"
          element={
            <AnimatedPage>
              <Privacy />
            </AnimatedPage>
          }
        />
        <Route
          path="/careers"
          element={
            <AnimatedPage>
              <Careers />
            </AnimatedPage>
          }
        />
        <Route
          path="/market"
          element={
            <PrivateRoute>
              <AnimatedPage>
                <BuyRentSell />
              </AnimatedPage>
            </PrivateRoute>
          }
        />
        <Route
          path="/market/listings"
          element={
            <PrivateRoute>
              <AnimatedPage>
                <MarketplaceListings />
              </AnimatedPage>
            </PrivateRoute>
          }
        />
        <Route
          path="/market/post"
          element={
            <PrivateRoute>
              <AnimatedPage>
                <PostListing />
              </AnimatedPage>
            </PrivateRoute>
          }
        />
        <Route
          path="/market/purchases"
          element={
            <PrivateRoute>
              <AnimatedPage>
                <PurchaseRequests />
              </AnimatedPage>
            </PrivateRoute>
          }
        />
        <Route
          path="/chats"
          element={
            <PrivateRoute>
              <AnimatedPage>
                <Chats />
              </AnimatedPage>
            </PrivateRoute>
          }
        />
        <Route
          path="/workers"
          element={
            <PrivateRoute>
              <AnimatedPage>
                <Workers />
              </AnimatedPage>
            </PrivateRoute>
          }
        />
        <Route
          path="/rooms"
          element={
            <PrivateRoute>
              <AnimatedPage>
                <Rooms />
              </AnimatedPage>
            </PrivateRoute>
          }
        />
        <Route
          path="/rooms/post"
          element={
            <PrivateRoute>
              <AnimatedPage>
                <PostRoom />
              </AnimatedPage>
            </PrivateRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <PrivateRoute>
              <AnimatedPage>
                <Profile />
              </AnimatedPage>
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/ads"
          element={
            <PrivateRoute>
              <AnimatedPage>
                <AdminAds />
              </AnimatedPage>
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/workers"
          element={
            <PrivateRoute>
              <AnimatedPage>
                <AdminVerifyWorkers />
              </AnimatedPage>
            </PrivateRoute>
          }
        />
      </Routes>
    </AnimatePresence>
  );
}

export default function App() {
  const { user, isVerified, logout } = useContext(AuthContext);
  return (
    <BrowserRouter>
      <div className="d-flex flex-column min-vh-100 bg-light">
        <Navbar
          bg="white"
          variant="light"
          expand="lg"
          className="shadow-sm sticky-top"
        >
          <Container>
            <Navbar.Brand
              href="/"
              className="fw-bold text-primary d-flex align-items-center"
            >
              <span className="fs-3 text-gradient">Rentalo</span>
            </Navbar.Brand>
            <Navbar.Toggle aria-controls="basic-navbar-nav" />
            <Navbar.Collapse id="basic-navbar-nav">
              <Nav className="me-auto">
                <Nav.Link href="/" className="d-flex align-items-center gap-1">
                  <FaHome /> Home
                </Nav.Link>
                <Nav.Link
                  href="/market"
                  className="d-flex align-items-center gap-1"
                >
                  <FaStore /> Market
                </Nav.Link>
                <Nav.Link
                  href="/workers"
                  className="d-flex align-items-center gap-1"
                >
                  <FaUsers /> Workers
                </Nav.Link>
                <Nav.Link
                  href="/rooms"
                  className="d-flex align-items-center gap-1"
                >
                  <FaBed /> Rooms
                </Nav.Link>
                {isVerified && user && (
                  <Nav.Link
                    href="/chats"
                    className="d-flex align-items-center gap-1"
                  >
                    <FaComments /> Messages
                  </Nav.Link>
                )}
              </Nav>
              <Nav>
                {isVerified && user ? (
                  <NavDropdown
                    title={
                      <span className="d-flex align-items-center gap-1">
                        <FaUser /> {user.username}
                      </span>
                    }
                    id="user-dropdown"
                    align="end"
                  >
                    <NavDropdown.ItemText>
                      Email: {user.email}
                    </NavDropdown.ItemText>
                    <NavDropdown.ItemText>
                      Phone: {user.phone}
                    </NavDropdown.ItemText>
                    <NavDropdown.ItemText>
                      State: {user.state}
                    </NavDropdown.ItemText>
                    <NavDropdown.ItemText>
                      City: {user.city}
                    </NavDropdown.ItemText>
                    {user?.isPrime ? (
                      <NavDropdown.ItemText className="text-warning fw-semibold d-flex align-items-center gap-2">
                        <FaCrown /> Prime Member
                      </NavDropdown.ItemText>
                    ) : (
                      <NavDropdown.Item
                        href="/profile#prime-membership"
                        className="d-flex align-items-center gap-2 text-warning fw-semibold"
                      >
                        <FaCrown /> Buy Prime for ₹100
                      </NavDropdown.Item>
                    )}
                    <NavDropdown.Divider />
                    <NavDropdown.Item href="/market/post">
                      <FaPlusCircle className="me-2" /> Post Item
                    </NavDropdown.Item>
                    <NavDropdown.Item href="/rooms/post">
                      <FaPlusCircle className="me-2" /> Post Room
                    </NavDropdown.Item>
                    <NavDropdown.Item href="/market/purchases">
                      View Purchase Requests
                    </NavDropdown.Item>
                    <NavDropdown.Divider />
                    <NavDropdown.Item href="/profile">
                      Edit Profile
                    </NavDropdown.Item>
                    <NavDropdown.Item onClick={logout}>Logout</NavDropdown.Item>
                  </NavDropdown>
                ) : (
                  <Nav.Link
                    href="/login"
                    className="d-flex align-items-center gap-1"
                  >
                    <FaSignInAlt /> Login
                  </Nav.Link>
                )}
              </Nav>
            </Navbar.Collapse>
          </Container>
        </Navbar>
        <Container className="flex-grow-1 py-4">
          <AnimatedRoutes />
        </Container>
        <footer className="footer-simple mt-auto py-4">
          <Container>
            <div className="row align-items-center">
              <div className="col-md-6 mb-3 mb-md-0 text-center text-md-start">
                <h5 className="fw-bold text-gradient mb-2">Rentalo</h5>
                <p className="text-muted small mb-0">
                  Your trusted rental marketplace
                </p>
              </div>
              <div className="col-md-6 text-center text-md-end">
                <div className="d-flex justify-content-center justify-content-md-end gap-4 mb-3 mb-md-0">
                  <a href="/" className="footer-link-simple">
                    Home
                  </a>
                  <a href="/market" className="footer-link-simple">
                    Market
                  </a>
                  <a href="/workers" className="footer-link-simple">
                    Workers
                  </a>
                  <a href="/rooms" className="footer-link-simple">
                    Rooms
                  </a>
                  <a href="/careers" className="footer-link-simple">
                    Careers
                  </a>
                  <a href="/privacy" className="footer-link-simple">
                    Privacy Policy
                  </a>
                  <a href="/terms" className="footer-link-simple">
                    Terms
                  </a>
                </div>
              </div>
            </div>
            <hr className="my-3" />
            <div className="row align-items-center">
              <div className="col-md-6 mb-2 mb-md-0 text-center text-md-start">
                <p className="text-muted small mb-0">
                  &copy; {new Date().getFullYear()} Rentalo. All rights
                  reserved.
                </p>
              </div>
              <div className="col-md-6 text-center text-md-end">
                <div className="d-flex justify-content-center justify-content-md-end gap-3">
                  <a
                    href="mailto:prateekmishrachs@gmail.com"
                    className="footer-link-simple"
                  >
                    <FaEnvelope size={16} />
                  </a>
                  <a href="tel:+918960825931" className="footer-link-simple">
                    <FaPhone size={16} />
                  </a>
                </div>
              </div>
            </div>
          </Container>
        </footer>
      </div>
    </BrowserRouter>
  );
}
